import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { cn } from '@bookrcs/ui';
import { useAuth } from '../lib/auth.js';

interface Tab {
  to: string;
  label: string;
  short: string;
}

const TABS: ReadonlyArray<Tab> = [
  { to: '/', label: "Aujourd'hui", short: 'Auj.' },
  { to: '/calendar', label: 'Calendrier', short: 'Cal.' },
  { to: '/messages', label: 'Messages', short: 'Msg' },
  { to: '/clients', label: 'Clients', short: 'Clt' },
  { to: '/studio', label: 'Studio', short: 'Std' },
  { to: '/pilotage', label: 'Pilotage', short: 'Pil.' },
];

export default function AppShell() {
  const { session, logout } = useAuth();
  const location = useLocation();
  const current = TABS.find((t) => t.to === location.pathname) ?? TABS[0];

  return (
    <div className="min-h-screen md:grid md:grid-cols-[228px_minmax(0,1fr)] bg-[var(--background)]">
      <aside className="hidden md:flex md:flex-col gap-2 p-6 bg-[var(--surface)] border-r border-[var(--border)]">
        <NavLink to="/" className="brand text-lg font-black mb-4 text-[var(--text)]">
          BOOKRCS
        </NavLink>
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === '/'}
            className={({ isActive }) =>
              cn(
                'px-3 py-3 rounded-[var(--radius-control)] font-bold transition-colors',
                isActive
                  ? 'bg-[var(--accent)] text-[var(--accent-foreground)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--elevated)]',
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
        <div className="mt-auto pt-4 border-t border-[var(--border)]">
          {session ? (
            <div className="text-sm text-[var(--text-muted)] mb-2 truncate">{session.proName}</div>
          ) : null}
          <button
            type="button"
            onClick={() => void logout()}
            className="text-sm text-[var(--text-faint)] hover:text-[var(--urgent)] transition-colors"
          >
            Se déconnecter
          </button>
        </div>
      </aside>

      <main className="flex flex-col min-h-screen">
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-[var(--surface)] border-b border-[var(--border)] sticky top-0 z-10">
          <span className="font-black text-base text-[var(--text)]">BOOKRCS</span>
          <span className="text-sm font-bold text-[var(--text-muted)]">{current?.label}</span>
        </header>

        <div className="flex-1 p-4 md:p-7 pb-24 md:pb-7">
          <Outlet />
        </div>

        <nav
          aria-label="Navigation"
          className="md:hidden fixed bottom-0 inset-x-0 bg-[var(--surface)] border-t border-[var(--border)] grid grid-cols-6 z-20"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-center min-h-[56px] text-xs font-bold',
                  isActive ? 'text-[var(--accent)]' : 'text-[var(--text-faint)]',
                )
              }
            >
              {tab.short}
            </NavLink>
          ))}
        </nav>
      </main>
    </div>
  );
}
